import { useEffect, useMemo, useState } from 'react';
import { useVimOsContext } from './useVimOsContext';
import type { EHR } from 'vim-os-js-browser/types';

export const useUpdateEncounter = (
  paramsToCheck?: EHR.CanUpdateEncounterParams
) => {
  const vimOS = useVimOsContext();

  const [canUpdateParams, setCanUpdateParams] = useState<boolean>(false);
  const [updateableDetails, setUpdateableDetails] =
    useState<EHR.CanUpdateEncounterParams>({});

  useEffect(() => {
    if (!vimOS?.ehr?.resourceUpdater) {
      return;
    }

    const refresh = () => {
      if (!paramsToCheck) {
        setCanUpdateParams(false);
        setUpdateableDetails({});
        return;
      }
      const result =
        vimOS.ehr.resourceUpdater.canUpdateEncounter(paramsToCheck);
      setCanUpdateParams(result.canUpdate);
      setUpdateableDetails(result.details);
    };

    refresh();

    vimOS.ehr.resourceUpdater.subscribe('encounter', refresh);
    return () => vimOS.ehr.resourceUpdater.unsubscribe('encounter', refresh);
  }, [vimOS?.ehr?.resourceUpdater, paramsToCheck]);

  return useMemo(
    () => ({
      canUpdateParams,
      updateableDetails,
      canUpdate: (params: EHR.CanUpdateEncounterParams) => {
        if (!vimOS?.ehr?.resourceUpdater) {
          return { canUpdate: false, details: { error: 'SDK not available' } };
        }
        return vimOS.ehr.resourceUpdater.canUpdateEncounter(params);
      },
      updateVimEncounter: async (params: EHR.UpdateEncounterParams) => {
        if (!vimOS?.ehr?.resourceUpdater) {
          throw new Error('Vim SDK not available');
        }
        return vimOS.ehr.resourceUpdater.updateEncounter(params);
      },
      isVimSDKAvailable: !!vimOS?.ehr?.resourceUpdater,
    }),
    [vimOS?.ehr?.resourceUpdater, canUpdateParams, updateableDetails]
  );
};
